import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToolaSyncService } from './toola-sync.service';

/**
 * "Toola Accounting Sync" view: summary cards + a per-bill detail table showing
 * which bills posted, which didn't, and the exact error. Filterable by status,
 * auto-refreshes every 15s. Drop <app-toola-sync-status> on any admin screen.
 */
@Component({
  selector: 'app-toola-sync-status',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Toola Accounting Sync</h6>
          <small class="text-muted" *ngIf="status?.lastSyncedAt">
            Last posted: {{ status.lastSyncedAt | date: 'short' }}
          </small>
        </div>

        <div *ngIf="loading" class="text-muted small">Loading…</div>
        <div *ngIf="!loading && status?.configured === false" class="text-muted small">
          Sync connector not configured.
        </div>
        <div *ngIf="!loading && status?.reachable === false" class="text-danger small">
          Sync connector unreachable.
        </div>

        <ng-container *ngIf="!loading && status && status.configured !== false && status.reachable !== false">
          <div class="row g-3">
            <div class="col-4">
              <div class="card text-center h-100"><div class="card-body py-3">
                <div class="h4 mb-0 text-success">{{ status?.synced ?? 0 }}</div>
                <div class="text-muted small">Posted to Toola</div>
              </div></div>
            </div>
            <div class="col-4">
              <div class="card text-center h-100"><div class="card-body py-3">
                <div class="h4 mb-0 text-warning">{{ status?.pending ?? 0 }}</div>
                <div class="text-muted small">Pending</div>
              </div></div>
            </div>
            <div class="col-4">
              <div class="card text-center h-100"><div class="card-body py-3">
                <div class="h4 mb-0" [class.text-danger]="(status?.failed ?? 0) > 0">{{ status?.failed ?? 0 }}</div>
                <div class="text-muted small">Failed</div>
              </div></div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 mb-2">
            <h6 class="mb-0">Bill detail — which posted, which didn't</h6>
            <select class="form-select form-select-sm" style="width:auto"
                    (change)="onFilter($any($event.target).value)">
              <option value="">All</option>
              <option value="DONE">Posted ✓</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="DEAD">Failed</option>
            </select>
          </div>

          <div class="table-responsive" style="max-height:360px;overflow:auto">
            <table class="table table-sm align-middle mb-0">
              <thead><tr>
                <th>Bill No</th><th>Date</th><th>Amount</th><th>Status</th><th>Toola / Error</th><th>Tries</th>
              </tr></thead>
              <tbody>
                <tr *ngFor="let it of items">
                  <td>{{ it.billNo || (it.sourceRef | slice: 0:8) }}</td>
                  <td class="text-muted">{{ it.billDate ? (it.billDate | slice: 0:10) : '—' }}</td>
                  <td>{{ it.amount != null ? '₹' + it.amount : '—' }}</td>
                  <td><span [ngClass]="statusColor(it.status)">{{ statusLabel(it.status) }}</span></td>
                  <td>
                    <span *ngIf="it.status === 'DONE'" class="text-muted small">Toola #{{ it.toolaDocId }}</span>
                    <span *ngIf="it.status !== 'DONE'" class="text-danger small">{{ it.lastError || '—' }}</span>
                  </td>
                  <td class="text-muted">{{ it.attempts }}</td>
                </tr>
                <tr *ngIf="!items.length">
                  <td colspan="6" class="text-muted text-center py-3">no bills</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="text-muted small mt-2" *ngIf="items.length">
            Showing {{ items.length }} of {{ itemsTotal }} bills
          </div>
        </ng-container>
      </div>
    </div>
  `,
})
export class ToolaSyncStatusComponent implements OnInit, OnDestroy {
  /** Optional: scope to a specific source client (Zenflow multi-tenant). */
  @Input() clientId?: string;

  status: any = null;
  items: any[] = [];
  itemsTotal = 0;
  filter = '';
  loading = true;
  private timer: any;

  constructor(private svc: ToolaSyncService) {}

  ngOnInit(): void {
    this.refresh();
    this.timer = setInterval(() => this.refresh(), 15000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  refresh(): void {
    this.svc.getStatus(this.clientId).subscribe({
      next: (s) => {
        this.status = s;
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
    this.loadItems();
  }

  loadItems(): void {
    this.svc.getItems(this.filter, this.clientId).subscribe({
      next: (d) => {
        this.items = d?.items || [];
        this.itemsTotal = d?.total || 0;
      },
      error: () => {},
    });
  }

  onFilter(v: string): void {
    this.filter = v;
    this.loadItems();
  }

  statusColor(s: string): string {
    if (s === 'DONE') return 'text-success';
    if (s === 'PENDING' || s === 'PROCESSING') return 'text-warning';
    return 'text-danger';
  }

  statusLabel(s: string): string {
    return s === 'DONE' ? 'Posted ✓' : s;
  }
}
